/**
 * The binomial, Poisson and hypergeometric calculators share one shape: a probability
 * mass function, its two tails, a mean and a variance, the six standard questions
 * (=, ≤, <, ≥, >, between) and a table. This module states each distribution once in
 * that shape so the three widgets differ only in their inputs and their prose.
 */

import {
  binomialUpper,
  hypergeometricUpper,
  poissonUpper,
} from "@/lib/stats/discrete-tails";
import {
  binomialCdf,
  binomialPmf,
  hypergeometricCdf,
  hypergeometricPmf,
  poissonCdf,
  poissonPmf,
} from "@/lib/stats/distributions";

export interface DiscreteModel {
  pmf: (k: number) => number;
  /** P(X ≤ k). */
  cdf: (k: number) => number;
  /** P(X ≥ k), computed directly rather than as 1 − P(X ≤ k − 1). */
  upper: (k: number) => number;
  mean: number;
  variance: number;
  /** Smallest and largest possible k; the Poisson's is unbounded. */
  support: [number, number];
}

/** Rows a table or bar chart may show before it stops being readable. */
export const MAX_TABLE_ROWS = 201;

export function binomialModel(n: number, p: number): DiscreteModel {
  return {
    pmf: (k) => binomialPmf(k, n, p),
    cdf: (k) => binomialCdf(k, n, p),
    upper: (k) => binomialUpper(k, n, p),
    mean: n * p,
    variance: n * p * (1 - p),
    support: [0, n],
  };
}

export function poissonModel(lambda: number): DiscreteModel {
  return {
    pmf: (k) => poissonPmf(k, lambda),
    cdf: (k) => poissonCdf(k, lambda),
    upper: (k) => poissonUpper(k, lambda),
    mean: lambda,
    variance: lambda,
    support: [0, Number.POSITIVE_INFINITY],
  };
}

export function hypergeometricModel(
  population: number,
  successes: number,
  draws: number
): DiscreteModel {
  const share = successes / population;
  const correction =
    population > 1 ? (population - draws) / (population - 1) : 0;
  return {
    pmf: (k) => hypergeometricPmf(k, population, successes, draws),
    cdf: (k) => hypergeometricCdf(k, population, successes, draws),
    upper: (k) => hypergeometricUpper(k, population, successes, draws),
    mean: draws * share,
    variance: draws * share * (1 - share) * correction,
    support: [
      Math.max(0, draws - (population - successes)),
      Math.min(successes, draws),
    ],
  };
}

export interface DiscreteProbabilities {
  equal: number;
  atMost: number;
  lessThan: number;
  atLeast: number;
  greaterThan: number;
  /** P(a ≤ X ≤ b) when a second value is given (the two are put in order). */
  between: number | null;
  range: [number, number] | null;
}

/** P(a ≤ X ≤ b), from whichever tail keeps the subtraction precise. */
export function probabilityBetween(
  model: DiscreteModel,
  a: number,
  b: number
): number {
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  const below = model.cdf(low - 1);
  const value =
    below > 0.5
      ? model.upper(low) - model.upper(high + 1)
      : model.cdf(high) - below;
  return Math.min(1, Math.max(0, value));
}

export function discreteProbabilities(
  model: DiscreteModel,
  k: number,
  second: number | null
): DiscreteProbabilities {
  return {
    equal: model.pmf(k),
    atMost: model.cdf(k),
    lessThan: model.cdf(k - 1),
    atLeast: model.upper(k),
    greaterThan: model.upper(k + 1),
    between: second === null ? null : probabilityBetween(model, k, second),
    range: second === null ? null : [Math.min(k, second), Math.max(k, second)],
  };
}

export interface TableRow {
  k: number;
  pmf: number;
  cdf: number;
  upper: number;
}

/**
 * The k values worth tabulating: the whole support when it is short, otherwise the
 * mean ± 6 SD (outside which nothing shows at four decimals), widened to include the values the
 * reader asked about when that still fits. Null when even the core is too long to show.
 */
export function tableRange(
  model: DiscreteModel,
  wanted: readonly number[]
): [number, number] | null {
  const [min, max] = model.support;
  const sd = Math.sqrt(model.variance);
  let from = Math.max(min, Math.floor(model.mean - 6 * sd) - 1);
  let to = Math.min(max, Math.ceil(model.mean + 6 * sd) + 2);
  if (max - min + 1 <= MAX_TABLE_ROWS) {
    from = min;
    to = max;
  }
  if (to - from + 1 > MAX_TABLE_ROWS) {
    return null;
  }
  const inside = wanted.filter((k) => k >= min && k <= max);
  const widenedFrom = Math.min(from, ...inside);
  const widenedTo = Math.max(to, ...inside);
  if (widenedTo - widenedFrom + 1 <= MAX_TABLE_ROWS) {
    return [widenedFrom, widenedTo];
  }
  return [from, to];
}

export function distributionTable(
  model: DiscreteModel,
  from: number,
  to: number
): TableRow[] {
  const rows: TableRow[] = [];
  for (let k = from; k <= to; k++) {
    rows.push({
      k,
      pmf: model.pmf(k),
      cdf: model.cdf(k),
      upper: model.upper(k),
    });
  }
  return rows;
}
